import { useState, useMemo } from 'react';
import { PlusIcon } from '@interstate/components/Icons';
import { Action } from '@interstate/components/Action';
import { TextInput } from '@makemydeal/dr-shared-components/text-input';
import { NumericInput } from '@makemydeal/dr-shared-components/numeric-input';
import type { InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { useCustomerCardContext } from '../../CustomerFormContext';
import { FieldsRow } from '../sectionLayout.styles';
import { AdditionalIncomeCard, LayoutHeaderRow } from './housingAndEmployment.styles';
import { Typography } from '@interstate/components/Typography';
import { Button } from '@interstate/components/Button';
import { TrashIcon } from '@interstate/components/Icons';
import { OTHER_INCOME_FIELD, OTHER_INCOME_SOURCE_FIELD, hasAnyFieldValue } from './housingAndEmployment.utils';
import { DEFAULT_ERROR_STATE, type ErrorState } from '@makemydeal/dr-dash-ui-utils';
import { CUSTOMER_CARD_ANALYTICS } from '../../../analytics';

export const AdditionalIncome = () => {
    const {
        formValues,
        formErrors,
        handleInputChange,
        handleNumberInputChange,
        handleInitAdditionalIncome,
        handleClearAdditionalIncome,
        isLocked,
        customerRole,
        onAnalyticsEvent
    } = useCustomerCardContext();

    const financialInfo = formValues.financialInfo;
    const finErrors = formErrors?.financialInfo as Record<string, ErrorState> | undefined;

    const errors = useMemo(
        () => ({
            otherIncomeSource: finErrors?.otherIncomeSource ?? DEFAULT_ERROR_STATE,
            otherIncome: finErrors?.otherIncome ?? DEFAULT_ERROR_STATE
        }),
        [finErrors]
    );

    const [expanded, setExpanded] = useState(() => {
        return hasAnyFieldValue(financialInfo?.otherIncome, financialInfo?.otherIncomeSource);
    });

    const handleDelete = () => {
        handleClearAdditionalIncome();
        setExpanded(false);
    };

    return (
        <div data-testid="additional-income-section">
            {!expanded ? (
                <Action
                    data-testid="additional-income-button"
                    actionIcon={{ icon: <PlusIcon />, start: true }}
                    sx={{ marginTop: '1rem' }}
                    onClick={() => {
                        onAnalyticsEvent?.(CUSTOMER_CARD_ANALYTICS.CLICKED_ADD_ADDITIONAL_INCOME, { customerRole });
                        handleInitAdditionalIncome();
                        setExpanded(true);
                    }}
                >
                    Add Additional Income
                </Action>
            ) : (
                <AdditionalIncomeCard>
                    <LayoutHeaderRow>
                        <Typography variant="h6">Additional Income</Typography>
                        <Button
                            size="sm"
                            buttonStyle="secondary"
                            sx={{ minWidth: '2rem' }}
                            onClick={handleDelete}
                            disabled={isLocked}
                            data-testid="additional-income-delete-button"
                        >
                            <TrashIcon />
                        </Button>
                    </LayoutHeaderRow>

                    <FieldsRow $columns={4} $mdColumns={2} $smColumns={1}>
                        <TextInput
                            name="otherIncomeSource"
                            displayLabel
                            label="Source of Other Income"
                            value={financialInfo?.otherIncomeSource ?? ''}
                            onChange={(e) => handleInputChange(OTHER_INCOME_SOURCE_FIELD, e.target.value)}
                            hasError={errors.otherIncomeSource.hasError}
                            errorMessage={errors.otherIncomeSource.errorMessage}
                            selectOnFocusSelector={featureToggleSelectors.isAutoSelectContentOnSlideOutsEnabledAfterBootstrap}
                            disabled={isLocked}
                            size="small"
                            data-testid="customer-form-other-income-source"
                        />

                        <NumericInput
                            name="otherIncome"
                            displayLabel
                            label="Amount"
                            value={financialInfo?.otherIncome}
                            onChange={(e: InterstateOnChangeEvent<string>) =>
                                handleNumberInputChange(OTHER_INCOME_FIELD, e.target.value)
                            }
                            allowDecimal
                            autoInsertCommas
                            decimalMaxLength={2}
                            decimalMinLength={0}
                            inputPrefix="$"
                            hasError={errors.otherIncome.hasError}
                            errorMessage={errors.otherIncome.errorMessage}
                            selectOnFocusSelector={featureToggleSelectors.isAutoSelectContentOnSlideOutsEnabledAfterBootstrap}
                            disabled={isLocked}
                            size="small"
                            data-testid="customer-form-other-income-amount"
                        />
                    </FieldsRow>
                </AdditionalIncomeCard>
            )}
        </div>
    );
};

export default AdditionalIncome;
